import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { EMAIL_SCHEMA } from './schemas';

interface ChangeTargetPanelProps {
  currentEmail: string;
  loading: boolean;
  serverError: string | null;
  onSubmit: (args: { email: string }) => void;
  onCancel: () => void;
}

/**
 * First step of a primary email change: asks for the new address and previews the change.
 *
 * @param props.serverError - Message from a failed preview, shown under the field.
 */
export function ChangeTargetPanel({
  currentEmail,
  loading,
  serverError,
  onSubmit,
  onCancel,
}: ChangeTargetPanelProps) {
  const schema = z.object({
    email: EMAIL_SCHEMA.refine(email => email !== currentEmail, {
      message: 'That is already the primary email.',
    }),
  });
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(({ email }) => onSubmit({ email }))}
        className="space-y-3 rounded-lg border border-primary p-4">
        <h3 className="font-semibold">Change primary email to a new address</h3>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-bold">New primary email</FormLabel>
              <FormControl>
                <Input
                  placeholder="name@hospital.org"
                  autoComplete="off"
                  disabled={loading}
                  {...field}
                />
              </FormControl>
              <FormMessage />
              {serverError && <p className="text-sm font-medium text-destructive">{serverError}</p>}
            </FormItem>
          )}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Checking...' : 'Preview change'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
